import { useEffect, useMemo, useState } from "react";
import { api, formatRupiah, dispDate, currentYM, dispDT } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageLoader } from "@/components/PageLoader";
import DashboardInsights from "@/components/DashboardInsights";
import {
  Users, UserCheck, UserX, Clock, AlertTriangle, Wallet,
  TrendingUp, Calendar, MapPin, ClipboardList, Briefcase,
  ArrowDownRight, ArrowUpRight, RefreshCw, FileText,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";
import { Link } from "react-router-dom";

const COLORS = {
  primary: "#0055FF",
  success: "#16a34a",
  warning: "#f59e0b",
  danger: "#dc2626",
  purple: "#9333ea",
  zinc: "#71717a",
};

const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;
const shortDate = (ymd) => ymd?.split("-").slice(1).reverse().join("-") || ymd;

export default function AdminDashboard() {
  const [period, setPeriod] = useState(currentYM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/dashboard/admin", { params: { period } })
      .then((r) => { if (!cancel) setData(r.data); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [period]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/dashboard/admin", { params: { period } });
      setData(r.data);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading && !data) return <PageLoader label="Memuat dashboard..." />;
  if (!data) return null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
        <div>
          <p className="overline mb-1">Dashboard</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Ringkasan Operasional</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Hari ini: <span className="font-mono">{dispDate(data.period.today)}</span> · Periode: {dispDate(data.period.start)} s.d. {dispDate(data.period.end)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-40"
            data-testid="dash-period"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={refresh}
            disabled={refreshing}
            data-testid="dash-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* TODAY ROW */}
      <SectionTitle title="Hari Ini" subtitle="Status presensi pegawai bulanan saat ini" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <KpiCard
          icon={UserCheck} label="Hadir" value={data.today.present}
          sub={`dari ${data.today.expected}`} color="success"
          tid="kpi-today-present"
        />
        <KpiCard
          icon={AlertTriangle} label="Terlambat" value={data.today.late}
          sub="orang" color="warning" tid="kpi-today-late"
        />
        <KpiCard
          icon={UserX} label="Belum Masuk" value={data.today.absent}
          sub="pegawai bulanan" color="danger" tid="kpi-today-absent"
        />
        <KpiCard
          icon={Calendar} label="Izin/Sakit" value={data.today.on_leave}
          sub="hari ini" color="purple" tid="kpi-today-leave"
        />
        <KpiCard
          icon={ArrowDownRight} label="Sudah Pulang" value={data.today.checked_out}
          sub="check-out" color="zinc" tid="kpi-today-out"
        />
        <KpiCard
          icon={TrendingUp} label="Rasio Hadir" value={fmtPct(data.today.attendance_rate)}
          sub="dari ekspektasi" color="primary" tid="kpi-today-rate"
        />
      </div>

      {/* HEADCOUNT */}
      <SectionTitle title="Komposisi Tim" subtitle={`${data.headcount.total_employees} pegawai aktif · ${data.headcount.active_locations}/${data.headcount.total_locations} lokasi aktif`} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <KpiCard icon={Users} label="Total Pegawai" value={data.headcount.total_employees} sub={`+${data.headcount.admins} admin`} color="primary" tid="kpi-emp-total" />
        <KpiCard icon={Briefcase} label="Bulanan" value={data.headcount.monthly} sub="staff tetap" color="zinc" tid="kpi-emp-monthly" />
        <KpiCard icon={Clock} label="PHL" value={data.headcount.hourly} sub="per-jam" color="zinc" tid="kpi-emp-hourly" />
        <KpiCard icon={MapPin} label="Lokasi Aktif" value={`${data.headcount.active_locations}/${data.headcount.total_locations}`} sub="geofence" color="zinc" tid="kpi-locs" />
      </div>

      {/* MONTH FINANCIAL */}
      <SectionTitle title="Periode Berjalan" subtitle="Akumulasi finansial & jam kerja" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <KpiCard icon={ArrowUpRight} label="Total Lembur" value={data.month.total_overtime_human} sub={formatRupiah(data.month.total_overtime_wage)} color="success" tid="kpi-m-ot" />
        <KpiCard icon={Clock} label="Jam PHL" value={data.month.total_phl_human} sub={formatRupiah(data.month.total_phl_wage)} color="primary" tid="kpi-m-phl" />
        <KpiCard icon={AlertTriangle} label="Total Telat" value={data.month.total_late_human} sub={`${data.month.total_late_days} hari`} color="warning" tid="kpi-m-late" />
        <KpiCard icon={ArrowDownRight} label="Potongan" value={formatRupiah(data.month.total_deduction)} sub="dari telat" color="danger" tid="kpi-m-ded" />
        <KpiCard icon={Wallet} label="Netto Periode" value={formatRupiah(data.month.total_netto)} sub="setelah potongan" color="purple" tid="kpi-m-netto" />
        <KpiCard icon={Calendar} label="Izin/Sakit" value={data.month.total_leaves} sub="hari" color="zinc" tid="kpi-m-leaves" />
      </div>

      {/* KASBON */}
      <SectionTitle title="Kasbon" subtitle={`${data.kasbon_month.total_count} pengajuan periode ini`} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <Card className="p-5 border border-zinc-200 shadow-none">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="overline mb-1">Pending Saat Ini</p>
              <p className="font-display text-2xl font-bold tracking-tight" data-testid="kpi-kasbon-pending-all">
                {data.kasbon_month.pending_all_count}
              </p>
              <p className="text-sm text-zinc-500 mt-1">{formatRupiah(data.kasbon_month.pending_all_amount)}</p>
            </div>
            <ClipboardList className="w-6 h-6 text-amber-500" />
          </div>
          {data.kasbon_month.pending_all_count > 0 && (
            <Link to="/admin/kasbon">
              <Button size="sm" variant="outline" className="w-full" data-testid="btn-go-kasbon">
                Tinjau Pengajuan →
              </Button>
            </Link>
          )}
        </Card>
        <Card className="p-5 border border-zinc-200 shadow-none">
          <p className="overline mb-3">Distribusi Status (Periode)</p>
          <KasbonDist
            pending={data.kasbon_month.pending_count}
            approved={data.kasbon_month.approved_count}
            rejected={data.kasbon_month.rejected_count}
          />
        </Card>
        <Card className="p-5 border border-zinc-200 shadow-none">
          <p className="overline mb-3">Nominal (Periode)</p>
          <div className="space-y-2 text-sm">
            <StatRow label="Disetujui" value={formatRupiah(data.kasbon_month.approved_amount)} color="text-green-700" />
            <StatRow label="Pending" value={formatRupiah(data.kasbon_month.pending_amount)} color="text-amber-700" />
            <StatRow label="Ditolak" value={formatRupiah(data.kasbon_month.rejected_amount)} color="text-red-700" />
            <div className="border-t border-zinc-200 pt-2 mt-2">
              <StatRow label="Total Diajukan" value={formatRupiah(data.kasbon_month.approved_amount + data.kasbon_month.pending_amount + data.kasbon_month.rejected_amount)} bold />
            </div>
          </div>
        </Card>
      </div>

      {/* TREND CHART */}
      <SectionTitle title="Tren Kehadiran" subtitle="Grafik harian periode ini" />
      <Card className="p-5 border border-zinc-200 shadow-none mb-8">
        <AttendanceTrend trend={data.attendance_trend} />
      </Card>

      {/* TOP LISTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <TopList
          title="Top Lembur"
          subtitle="Pegawai dengan jam lembur terbanyak"
          icon={ArrowUpRight}
          rows={data.top_overtime}
          renderRight={(r) => (
            <div className="text-right">
              <p className="text-sm font-semibold text-green-700">{r.human}</p>
              <p className="text-xs text-zinc-500">{formatRupiah(r.wage)}</p>
            </div>
          )}
          empty="Belum ada lembur"
          tid="top-overtime"
        />
        <TopList
          title="Top Keterlambatan"
          subtitle="Pegawai paling sering telat"
          icon={AlertTriangle}
          rows={data.top_late}
          renderRight={(r) => (
            <div className="text-right">
              <p className="text-sm font-semibold text-red-700">{r.human}</p>
              <p className="text-xs text-zinc-500">{r.days} hari · {formatRupiah(r.deduction)}</p>
            </div>
          )}
          empty="Tidak ada keterlambatan"
          tid="top-late"
        />
        <TopList
          title="Top PHL"
          subtitle="Pegawai PHL dengan jam kerja terbanyak"
          icon={Clock}
          rows={data.top_phl}
          renderRight={(r) => (
            <div className="text-right">
              <p className="text-sm font-semibold text-primary">{r.human}</p>
              <p className="text-xs text-zinc-500">{formatRupiah(r.wage)}</p>
            </div>
          )}
          empty="Belum ada PHL aktif"
          tid="top-phl"
        />
        <TopList
          title="Top Kasbon"
          subtitle="Total kasbon disetujui per pegawai"
          icon={Wallet}
          rows={data.top_kasbon}
          renderRight={(r) => (
            <div className="text-right">
              <p className="text-sm font-semibold text-purple-700">{formatRupiah(r.amount)}</p>
              <p className="text-xs text-zinc-500">{r.count}x diajukan</p>
            </div>
          )}
          empty="Tidak ada kasbon disetujui"
          tid="top-kasbon"
        />
      </div>

      {/* NOT CHECKED IN & RECENT ACTIVITY */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <Card className="p-5 border border-zinc-200 shadow-none">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="overline">Belum Absen Hari Ini</p>
              <h3 className="font-display text-lg font-semibold">Pegawai Bulanan</h3>
            </div>
            <Badge variant={data.not_checked_in_today.length > 0 ? "destructive" : "secondary"}>
              {data.not_checked_in_today.length}
            </Badge>
          </div>
          {data.not_checked_in_today.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">Semua pegawai bulanan sudah absen 🎉</p>
          ) : (
            <ul className="divide-y divide-zinc-100 max-h-64 overflow-y-auto" data-testid="list-not-checked-in">
              {data.not_checked_in_today.map((u) => (
                <li key={u.user_id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <p className="font-medium">{u.name}</p>
                    <p className="text-xs text-zinc-500 font-mono">{u.employee_id}</p>
                  </div>
                  <Link to="/admin/absen">
                    <Button size="sm" variant="ghost" className="text-xs h-7">Absenkan</Button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5 border border-zinc-200 shadow-none">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="overline">Aktivitas Terbaru</p>
              <h3 className="font-display text-lg font-semibold">Real-time Feed</h3>
            </div>
          </div>
          <ul className="divide-y divide-zinc-100 max-h-80 overflow-y-auto" data-testid="recent-activity">
            {data.recent_activity.length === 0 && (
              <li className="text-sm text-zinc-500 py-4 text-center">Belum ada aktivitas</li>
            )}
            {data.recent_activity.map((a, i) => (
              <li key={i} className="flex items-start gap-3 py-2.5">
                <ActivityIcon type={a.type} status={a.status} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="font-medium">{a.user_name}</span>{" "}
                    <span className="text-zinc-600">{describeActivity(a)}</span>
                  </p>
                  <p className="text-xs text-zinc-400 font-mono">{dispDT(a.time)}</p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* LOCATION & LEAVE TYPES */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <Card className="p-5 border border-zinc-200 shadow-none">
          <p className="overline mb-1">Distribusi Lokasi</p>
          <h3 className="font-display text-lg font-semibold mb-4">Check-in per Lokasi</h3>
          {data.location_distribution.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">Belum ada data lokasi</p>
          ) : (
            <LocationChart data={data.location_distribution} />
          )}
        </Card>
        <Card className="p-5 border border-zinc-200 shadow-none">
          <p className="overline mb-1">Tipe Izin</p>
          <h3 className="font-display text-lg font-semibold mb-4">Distribusi Izin/Sakit/Cuti</h3>
          <LeaveTypes data={data.month.leaves_by_type} />
        </Card>
      </div>

      {/* INSIGHTS — Analitik Tambahan (Alerts, Trend 6m, Punctuality, Financial, Loans) */}
      <DashboardInsights period={period} />

      {/* QUICK LINKS */}
      <SectionTitle title="Akses Cepat" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <QuickLink to="/admin/absen" icon={ClipboardList} label="Absenkan" />
        <QuickLink to="/admin/rekap" icon={FileText} label="Rekap Lengkap" />
        <QuickLink to="/admin/kasbon" icon={Wallet} label="Kasbon" />
        <QuickLink to="/admin/izin" icon={Calendar} label="Izin/Sakit" />
        <QuickLink to="/admin/slip" icon={FileText} label="Slip Gaji" />
        <QuickLink to="/admin/lokasi" icon={MapPin} label="Lokasi" />
      </div>
    </div>
  );
}

/* ============ Sub Components ============ */

const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-3">
    <h2 className="font-display text-base md:text-lg font-semibold tracking-tight">{title}</h2>
    {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
  </div>
);

const colorMap = {
  primary: { bg: "bg-blue-50", text: "text-blue-700", icon: "text-blue-600" },
  success: { bg: "bg-green-50", text: "text-green-700", icon: "text-green-600" },
  warning: { bg: "bg-amber-50", text: "text-amber-700", icon: "text-amber-600" },
  danger: { bg: "bg-red-50", text: "text-red-700", icon: "text-red-600" },
  purple: { bg: "bg-purple-50", text: "text-purple-700", icon: "text-purple-600" },
  zinc: { bg: "bg-zinc-100", text: "text-zinc-700", icon: "text-zinc-500" },
};

const KpiCard = ({ icon: Icon, label, value, sub, color = "zinc", tid }) => {
  const c = colorMap[color] || colorMap.zinc;
  return (
    <Card className="p-4 border border-zinc-200 shadow-none" data-testid={tid}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-8 h-8 ${c.bg} rounded-sm flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${c.icon}`} />
        </div>
      </div>
      <p className="overline mb-1 text-[0.6rem]">{label}</p>
      <p className="font-display text-xl md:text-2xl font-bold tracking-tight leading-tight">
        {value}
      </p>
      {sub && <p className="text-xs text-zinc-500 mt-0.5 truncate">{sub}</p>}
    </Card>
  );
};

const StatRow = ({ label, value, color = "", bold = false }) => (
  <div className="flex items-center justify-between">
    <span className={`text-zinc-600 ${bold ? "font-medium" : ""}`}>{label}</span>
    <span className={`font-mono ${color} ${bold ? "font-bold" : ""}`}>{value}</span>
  </div>
);

const KasbonDist = ({ pending, approved, rejected }) => {
  const total = pending + approved + rejected;
  if (total === 0) {
    return <p className="text-sm text-zinc-500 text-center py-6">Belum ada pengajuan kasbon</p>;
  }
  const chartData = [
    { name: "Disetujui", value: approved, color: COLORS.success },
    { name: "Pending", value: pending, color: COLORS.warning },
    { name: "Ditolak", value: rejected, color: COLORS.danger },
  ].filter((d) => d.value > 0);
  return (
    <div style={{ width: "100%", height: 160 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={chartData} dataKey="value" innerRadius={40} outerRadius={70} paddingAngle={2}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip />
          <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const AttendanceTrend = ({ trend }) => {
  const data = useMemo(
    () => trend.map((d) => ({ ...d, label: shortDate(d.date) })),
    [trend]
  );
  return (
    <div style={{ width: "100%", height: 280 }} data-testid="chart-trend">
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gPresent" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.35} />
              <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gLate" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.warning} stopOpacity={0.35} />
              <stop offset="95%" stopColor={COLORS.warning} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gLeave" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.35} />
              <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip
            labelFormatter={(label, p) => p?.[0]?.payload?.date ? dispDate(p[0].payload.date) : label}
            contentStyle={{ fontSize: 12, borderRadius: 4 }}
          />
          <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
          <Area type="monotone" dataKey="present" name="Hadir" stroke={COLORS.primary} fill="url(#gPresent)" strokeWidth={2} />
          <Area type="monotone" dataKey="late" name="Telat" stroke={COLORS.warning} fill="url(#gLate)" strokeWidth={2} />
          <Area type="monotone" dataKey="on_leave" name="Izin" stroke={COLORS.purple} fill="url(#gLeave)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const TopList = ({ title, subtitle, icon: Icon, rows, renderRight, empty, tid }) => (
  <Card className="p-5 border border-zinc-200 shadow-none" data-testid={tid}>
    <div className="flex items-center justify-between mb-3">
      <div>
        <p className="overline">{title}</p>
        <h3 className="font-display text-lg font-semibold">{subtitle}</h3>
      </div>
      <Icon className="w-5 h-5 text-zinc-400" />
    </div>
    {rows.length === 0 ? (
      <p className="text-sm text-zinc-500 text-center py-6">{empty}</p>
    ) : (
      <ul className="divide-y divide-zinc-100">
        {rows.map((r, i) => (
          <li key={i} className="flex items-center justify-between py-2.5">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-700 text-xs font-bold flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <p className="text-sm font-medium truncate">{r.name}</p>
            </div>
            {renderRight(r)}
          </li>
        ))}
      </ul>
    )}
  </Card>
);

const LocationChart = ({ data }) => {
  const chart = data.map((d) => ({ name: d.name, checkins: d.checkins }));
  return (
    <div style={{ width: "100%", height: 220 }} data-testid="chart-location">
      <ResponsiveContainer>
        <BarChart data={chart} layout="vertical" margin={{ left: 10, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
          <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
          <Bar dataKey="checkins" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const LeaveTypes = ({ data }) => {
  const entries = Object.entries(data || {});
  if (entries.length === 0) {
    return <p className="text-sm text-zinc-500 text-center py-6">Tidak ada izin pada periode ini</p>;
  }
  const palette = [COLORS.purple, COLORS.warning, COLORS.primary, COLORS.success, COLORS.zinc];
  const chartData = entries.map(([k, v], i) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1),
    value: v,
    color: palette[i % palette.length],
  }));
  return (
    <div style={{ width: "100%", height: 220 }} data-testid="chart-leaves">
      <ResponsiveContainer>
        <PieChart>
          <Pie data={chartData} dataKey="value" outerRadius={80} label={(e) => `${e.name}: ${e.value}`} labelLine={false}>
            {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const ActivityIcon = ({ type, status }) => {
  if (type === "attendance_in") {
    return <span className="w-7 h-7 rounded-full bg-green-50 text-green-600 flex items-center justify-center shrink-0"><ArrowUpRight className="w-4 h-4" /></span>;
  }
  if (type === "attendance_out") {
    return <span className="w-7 h-7 rounded-full bg-zinc-100 text-zinc-600 flex items-center justify-center shrink-0"><ArrowDownRight className="w-4 h-4" /></span>;
  }
  if (type === "kasbon") {
    const cls = status === "approved" ? "bg-green-50 text-green-600"
      : status === "rejected" ? "bg-red-50 text-red-600"
      : "bg-amber-50 text-amber-600";
    return <span className={`w-7 h-7 rounded-full ${cls} flex items-center justify-center shrink-0`}><Wallet className="w-4 h-4" /></span>;
  }
  return <span className="w-7 h-7 rounded-full bg-zinc-100 flex items-center justify-center shrink-0" />;
};

const describeActivity = (a) => {
  if (a.type === "attendance_in") return "absen masuk";
  if (a.type === "attendance_out") return "absen pulang";
  if (a.type === "kasbon") {
    const lbl = a.status === "approved" ? "kasbon disetujui" : a.status === "rejected" ? "kasbon ditolak" : "ajukan kasbon";
    return `${lbl} (${formatRupiah(a.amount)})`;
  }
  return "";
};

const QuickLink = ({ to, icon: Icon, label }) => (
  <Link to={to} data-testid={`quick-${to.replace(/\//g, "-")}`}>
    <Card className="p-4 border border-zinc-200 shadow-none hover:border-primary hover:bg-blue-50/40 transition-colors cursor-pointer">
      <Icon className="w-5 h-5 text-zinc-700 mb-2" />
      <p className="text-sm font-medium">{label}</p>
    </Card>
  </Link>
);
